/**
 * @fileoverview Billing MFA step-up gate (MOR-438)
 *
 * Wraps the Billing admin view. Nothing renders — and, more importantly,
 * nothing is *fetched* — until the server confirms a live WorkOS MFA
 * step-up, because every billing query rejects without one.
 *
 * The gate is a convenience, not the security boundary: the real enforcement
 * is `requireBillingAdmin` in convex/admin/billingAccess.ts. Bypassing this
 * component client-side yields nothing.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { extractErrorMessage } from "@/utils/errors";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck } from "lucide-react";

function minutesUntil(ts: number): number {
  return Math.max(0, Math.ceil((ts - Date.now()) / 60000));
}

export function BillingStepUpGate({ children }: { children: ReactNode }) {
  const { data: state, isPending } = useQuery({
    ...convexQuery(api.admin.billingAccess.getStepUpState, {}),
    // The unlock expires on a timer, so re-check periodically rather than
    // letting the UI drift out of sync with the server.
    refetchInterval: 60_000,
  });

  const [code, setCode] = useState("");
  const [isWorking, setIsWorking] = useState(false);
  const [challengeIssued, setChallengeIssued] = useState(false);
  const [notEnrolled, setNotEnrolled] = useState(false);

  const startStepUp = useAction(api.admin.billingMfa.startStepUp);
  const verifyStepUp = useAction(api.admin.billingMfa.verifyStepUp);
  const lockBilling = useMutation(api.admin.billingAccess.lockBilling);

  const allowed = state?.allowed ?? false;
  const unlocked = state?.unlocked ?? false;

  // Ask WorkOS for a challenge as soon as we know the user is allowed but
  // not yet stepped up, so the code box is live on arrival.
  useEffect(() => {
    if (!allowed || unlocked || challengeIssued || notEnrolled) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await startStepUp({});
        if (cancelled) return;
        if (!result.enrolled) setNotEnrolled(true);
        else if (result.challengeIssued) setChallengeIssued(true);
      } catch (error) {
        if (!cancelled) {
          toast.error(
            extractErrorMessage(error, "Could not start verification."),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, unlocked, challengeIssued, notEnrolled, startStepUp]);

  if (isPending) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking access…
      </div>
    );
  }

  if (!allowed) {
    return (
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4" />
            Restricted
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Billing is limited to specific accounts. If you believe you should
          have access, it has to be granted explicitly — it is not implied by
          admin rights.
        </CardContent>
      </Card>
    );
  }

  if (unlocked) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
          Verified
          {state?.unlockedUntil && (
            <>· expires in {minutesUntil(state.unlockedUntil)} min</>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={async () => {
              await lockBilling({});
              setChallengeIssued(false);
              setCode("");
            }}
          >
            Lock now
          </Button>
        </div>
        {children}
      </div>
    );
  }

  const lockedOutUntil = state?.lockedOutUntil ?? null;

  const handleVerify = async () => {
    setIsWorking(true);
    try {
      const result = await verifyStepUp({ code });
      if (result.verified) {
        toast.success("Verified.");
        setCode("");
      } else {
        toast.error(result.reason ?? "That code was not accepted.");
        // Challenges are single-use — a failure invalidates it.
        setChallengeIssued(false);
        setCode("");
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Verification failed."));
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock className="h-4 w-4" />
          Verify it&apos;s you
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Billing shows revenue and margins across every firm, so it needs a
          fresh authenticator code even though you&apos;re already signed in.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {notEnrolled ? (
          <p className="text-sm text-muted-foreground">
            No authenticator app is enrolled on your WorkOS account yet. Add one
            in your account security settings, then reload this page.
          </p>
        ) : lockedOutUntil ? (
          <p className="text-sm text-destructive">
            Too many failed attempts. Try again in{" "}
            {minutesUntil(lockedOutUntil)} min.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <Label htmlFor="mfa-code">6-digit code</Label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length === 6) {
                    void handleVerify();
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleVerify}
                disabled={isWorking || code.length !== 6 || !challengeIssued}
              >
                {isWorking ? "Verifying…" : "Unlock billing"}
              </Button>
              {!challengeIssued && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isWorking}
                  onClick={async () => {
                    setIsWorking(true);
                    try {
                      const r = await startStepUp({});
                      if (!r.enrolled) setNotEnrolled(true);
                      else if (r.challengeIssued) setChallengeIssued(true);
                    } catch (error) {
                      toast.error(
                        extractErrorMessage(error, "Could not start."),
                      );
                    } finally {
                      setIsWorking(false);
                    }
                  }}
                >
                  Request a new code
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
